import { useCallback, useState, DragEvent } from 'react';

import { fetchSaveUploadedDocumentsEdge } from '@api/lendingAPI/LendingAPI';
import { IconFilePlusInside, IconUpload } from '@iwoca/icons';
import { PostDocumentUploadResponse } from '@iwoca/lapi-client/edge';
import classNames from 'classnames';
import { v4 } from 'uuid';

import styles from './DragDropUpload.module.css';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { TUploadRejectReasonCode } from '../hooks/useFileUploadReducer';

type TDocumentType = PostDocumentUploadResponse['data']['document_type'];

const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];

const isValidFileType = (file: File): boolean => {
    const fileName = file.name.toLowerCase();
    return ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext));
};

type TProps = {
    documentType?: TDocumentType;
    description?: string;
    listOfInstructions: React.ReactNode[];
    setFileLoading: (id: string, name: string) => void;
    setFileSuccess: (id: string) => void;
    setFileError: (id: string, errorReason: string) => void;
    setFileReject: (id: string, rejectReason: TUploadRejectReasonCode) => void;
    isError?: boolean;
};

export const DragDropUpload = ({
    documentType = 'other',
    description,
    listOfInstructions,
    setFileLoading,
    setFileSuccess,
    setFileError,
    setFileReject,
    isError = false
}: TProps) => {
    const [isDragging, setIsDragging] = useState(false);
    const stateKey = useStateKey();

    const handleFiles = useCallback(
        async (files: FileList) => {
            if (!stateKey) return;

            await Promise.all(
                Array.from(files).map(async (file) => {
                    const uuid = v4();
                    setFileLoading(uuid, file.name);

                    if (!isValidFileType(file)) {
                        setFileError(uuid, 'File format incorrect');
                        return;
                    }

                    const body = new FormData();
                    body.append('document', file);
                    body.append('document_type', documentType);

                    try {
                        const response = await fetchSaveUploadedDocumentsEdge({
                            impersonatingStateKey: stateKey,
                            body: body as FormData
                        });

                        if (response?.[0]?.errors?.[0]) {
                            setFileError(uuid, response[0].errors[0].detail);
                        } else if (
                            response?.[0]?.attributes?.status === 'rejected'
                        ) {
                            setFileReject(
                                uuid,
                                response[0].attributes.reject_reason
                            );
                        } else {
                            setFileSuccess(uuid);
                        }
                    } catch {
                        setFileError(uuid, 'Upload failed');
                    }
                })
            );
        },
        [
            stateKey,
            documentType,
            setFileLoading,
            setFileSuccess,
            setFileError,
            setFileReject
        ]
    );

    const handleDrag = useCallback(
        (isDragging: boolean) => (e: DragEvent<HTMLLabelElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(isDragging);
        },
        []
    );

    const handleDrop = useCallback(
        (e: DragEvent<HTMLLabelElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);

            const { files } = e.dataTransfer;
            if (files?.length) {
                handleFiles(files);
            }
        },
        [handleFiles]
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        handleFiles(e.target.files);
        // Reset so re-uploading the same file fires onChange again.
        e.target.value = '';
    };

    return (
        <div className={styles.container}>
            <label
                htmlFor={`drag-drop-input-${documentType}`}
                className={classNames(styles.dropzone, {
                    [styles.dropzoneActive]: isDragging,
                    [styles.dropzoneError]: isError
                })}
                onDragOver={handleDrag(true)}
                onDragLeave={handleDrag(false)}
                onDrop={handleDrop}
                data-testid="drag-drop-zone"
            >
                <div className={styles.uploadRow}>
                    {isDragging ? (
                        <div className={styles.uploadButton}>
                            <IconFilePlusInside className={styles.uploadIcon} />
                            <span className={styles.uploadText}>
                                Drop to upload
                            </span>
                        </div>
                    ) : (
                        <>
                            <div className={styles.uploadButton}>
                                <IconUpload className={styles.uploadIcon} />
                                <span className={styles.uploadText}>
                                    Upload
                                </span>
                            </div>
                            <span className={styles.dropText}>
                                or drag & drop files here
                            </span>
                        </>
                    )}
                </div>

                <div className={styles.instructionsContainer}>
                    {description && (
                        <p className={styles.description}>{description}</p>
                    )}
                    <ul className={styles.requirementsList}>
                        {listOfInstructions.map((instruction, index) => (
                            <li key={index}>{instruction}</li>
                        ))}
                    </ul>
                </div>

                <input
                    id={`drag-drop-input-${documentType}`}
                    data-testid="drag-drop-input"
                    aria-label="upload-area"
                    multiple
                    hidden
                    type="file"
                    accept=".jpg, .jpeg, .png, .pdf"
                    onChange={handleInputChange}
                    className="fs-mask"
                />
            </label>
        </div>
    );
};
